/**
 * Portada: banners del carrusel y que se destaca (ADM-05, ADM-06).
 *
 * Es una sola pantalla y no tres porque las tres cosas responden a la misma
 * pregunta —que ve alguien que entra al sitio— y se cambian juntas cuando
 * arranca una campana.
 *
 * Los productos se marcan desde la tabla de Productos, que ya tiene buscador y
 * paginacion sobre trescientos y tantos articulos; aca solo se ven los que hoy
 * estan arriba y se pueden bajar. Duplicar ese buscador para lo mismo habria
 * sido tener dos catalogos en el backoffice.
 *
 * Las categorias, en cambio, se buscan aca: son casi doscientas y no tienen otra
 * pantalla propia. La lista muestra siempre las marcadas, y el buscador es para
 * encontrar la que falta.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';

import type {
  ActualizarBannerDto,
  ActualizarSeoDto,
  Banner,
  CategoriaAdminResumen,
  ConfiguracionPortada,
  CrearBannerDto,
} from '@elcarpintero/compartido';

import { BarraAdmin } from '../../componentes/administracion/BarraAdmin';
import { FormularioBanner } from '../../componentes/administracion/FormularioBanner';
import { FormularioSeo } from '../../componentes/administracion/FormularioSeo';
import { FotoTiendaPortada } from '../../componentes/administracion/FotoTiendaPortada';
import { MaximoDestacados } from '../../componentes/administracion/MaximoDestacados';
import { Modal } from '../../componentes/administracion/Modal';
import { PanelAdmin } from '../../componentes/administracion/PanelAdmin';
import { Tabla } from '../../componentes/administracion/Tabla';
import { AvisoError, Cargando } from '../../componentes/comunes/EstadoPeticion';
import { Icono } from '../../componentes/comunes/Icono';
import { Imagen } from '../../componentes/comunes/Imagen';
import { usePeticion } from '../../hooks/use-peticion';
import { Seo } from '../../seo/Seo';
import {
  actualizarBanner,
  actualizarSeoCategoria,
  crearBanner,
  destacarCategoria,
  destacarProducto,
  eliminarBanner,
  moverBanner,
  obtenerBanners,
  obtenerCategoriasAdmin,
  obtenerConfiguracionPortada,
  obtenerProductosDestacados,
} from '../../servicios/portada.servicio';
import { NOMBRE_UBICACION, agruparBanners } from '../../utilidades/banners';
import { numero } from '../../utilidades/formato';
import { mensajeDe } from '../../utilidades/mensaje-error';
import { rutas } from '../../utilidades/rutas';

/**
 * Cuantas categorias entran en la portada. Es copia de
 * `CANTIDAD_CATEGORIAS_PORTADA` del backend: el paquete compartido lleva solo
 * tipos, asi que una constante de ejecucion no puede vivir alli. Si cambia
 * alla, cambia aca. El de productos lo elige el administrador
 * (`MaximoDestacados`).
 */
const CATEGORIAS_EN_PORTADA = 12;

/** Cuantas coincidencias se muestran al buscar una categoria. */
const RESULTADOS_CATEGORIA = 8;

export function Portada() {
  // Cambia despues de cada operacion y obliga a los hooks a pedir de nuevo.
  const [version, setVersion] = useState(0);
  const refrescar = (): void => {
    setVersion((valor) => valor + 1);
  };

  const banners = usePeticion((senal) => obtenerBanners(senal), [version]);
  const destacados = usePeticion((senal) => obtenerProductosDestacados(senal), [version]);
  const categorias = usePeticion((senal) => obtenerCategoriasAdmin(senal), [version]);
  // Sin `version`: guardar el maximo no cambia nada mas, y volver a pedirla
  // desmontaria el formulario junto con su "Guardado".
  const configuracionInicial = usePeticion((senal) => obtenerConfiguracionPortada(senal), []);
  const [configuracionGuardada, setConfiguracionGuardada] = useState<ConfiguracionPortada | null>(
    null,
  );

  const [editando, setEditando] = useState<Banner | null>(null);
  const [formAbierto, setFormAbierto] = useState(false);
  const [porEliminar, setPorEliminar] = useState<Banner | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [falla, setFalla] = useState<string | null>(null);
  const [busquedaCategoria, setBusquedaCategoria] = useState('');
  // Categoria cuyo modal de SEO esta abierto (ADM-08); null lo mantiene cerrado.
  const [editandoSeoCategoria, setEditandoSeoCategoria] = useState<CategoriaAdminResumen | null>(
    null,
  );

  const lista: Banner[] = banners.datos ?? [];
  const activos = lista.filter((banner) => banner.activo).length;
  // Subir y bajar se mueven dentro de la fila del banner, no de la lista entera.
  const porFila = agruparBanners(lista);
  const productos = destacados.datos ?? [];
  const configuracion = configuracionGuardada ?? configuracionInicial.datos;
  const maximoDestacados = configuracion?.maximoDestacados ?? null;
  const fueraDePortada = (indice: number): boolean =>
    maximoDestacados !== null && indice >= maximoDestacados;
  const todasCategorias: CategoriaAdminResumen[] = categorias.datos ?? [];
  const marcadas = todasCategorias.filter((categoria) => categoria.destacada);

  const termino = busquedaCategoria.trim().toLowerCase();
  const coincidencias =
    termino.length === 0
      ? []
      : todasCategorias
          .filter(
            (categoria) =>
              !categoria.destacada && categoria.nombre.toLowerCase().includes(termino),
          )
          .slice(0, RESULTADOS_CATEGORIA);

  /** Toda operacion pasa por aca: hace, limpia el error y vuelve a pedir. */
  async function operar(accion: () => Promise<unknown>): Promise<void> {
    setFalla(null);
    setEnviando(true);

    try {
      await accion();
      refrescar();
    } catch (fallo) {
      setFalla(mensajeDe(fallo));
    } finally {
      setEnviando(false);
    }
  }

  /**
   * Dar de alta y editar tienen datos distintos —la pieza es obligatoria en uno
   * y opcional en el otro (ADM-04)— y por eso el formulario avisa por dos
   * puertas separadas en vez de una sola con los campos a medias.
   */
  async function crearBannerNuevo(datos: CrearBannerDto): Promise<void> {
    await operar(async () => {
      await crearBanner(datos);
      setFormAbierto(false);
    });
  }

  async function editarBanner(datos: ActualizarBannerDto): Promise<void> {
    const enEdicion = editando;
    if (enEdicion === null) return;

    await operar(async () => {
      await actualizarBanner(enEdicion.id, datos);
      setFormAbierto(false);
    });
  }

  async function guardarSeoCategoria(datos: ActualizarSeoDto): Promise<void> {
    const categoria = editandoSeoCategoria;
    if (categoria === null) return;

    await operar(async () => {
      await actualizarSeoCategoria(categoria.id, datos);
      setEditandoSeoCategoria(null);
    });
  }

  return (
    <>
      <Seo
        titulo="Portada"
        descripcion="Banners y destacados de la portada."
        ruta={rutas.administracion.portada()}
        sinIndexar
      />

      <BarraAdmin
        titulo="Portada"
        bajada="Lo primero que ve alguien que entra al sitio"
        acciones={
          <button
            type="button"
            className="btn btn-sm btn-primario"
            onClick={() => {
              setEditando(null);
              setFalla(null);
              setFormAbierto(true);
            }}
          >
            <Icono nombre="mas" />
            Agregar banner
          </button>
        }
      />

      <div className="admin-cuerpo">
        {falla !== null && !formAbierto && porEliminar === null && editandoSeoCategoria === null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        {/* ---------- banners (ADM-05) ---------- */}

        <PanelAdmin
          titulo="Banners de la portada"
          bajada={`${activos} ${activos === 1 ? 'banner activo' : 'banners activos'} de ${lista.length}, en tres filas`}
          variante="plano"
        >
          {banners.estado === 'cargando' ? <Cargando que="los banners" /> : null}
          {banners.estado === 'error' ? <AvisoError error={banners.error} /> : null}

          {banners.datos !== null && lista.length === 0 ? (
            <p className="parrafo-panel">
              No hay banners cargados. La portada funciona igual, pero arranca sin banners.
            </p>
          ) : null}

          {lista.length > 0 ? (
            <Tabla
              titulo="Banners de la portada"
              encabezados={
                <>
                  <th scope="col" className="col-principal">
                    Banner
                  </th>
                  <th scope="col">Fila</th>
                  <th scope="col">Lleva a</th>
                  <th scope="col" className="cen">
                    Orden
                  </th>
                  <th scope="col">Estado</th>
                  <th scope="col" className="der">
                    Acción
                  </th>
                </>
              }
            >
              {lista.map((banner) => {
                const fila = porFila[banner.ubicacion];
                const posicion = fila.indexOf(banner);

                return (
                  <tr key={banner.id}>
                    <th scope="row">
                      <div className="celda-prod">
                        <Imagen
                          className="mini-banner"
                          src={banner.imagenUrl}
                          alt=""
                          ancho={banner.ancho}
                          alto={banner.alto}
                        />
                        <span>
                          <span className="n">{banner.titulo ?? banner.textoAlternativo}</span>
                          <span className="s">
                            {banner.ancho} x {banner.alto} px
                          </span>
                        </span>
                      </div>
                    </th>
                    <td>{NOMBRE_UBICACION[banner.ubicacion]}</td>
                    <td className="celda-enlace">
                      {banner.enlace !== null ? (
                        <span className="mono">{banner.enlace}</span>
                      ) : (
                        <span className="celda-sub">No se puede pulsar</span>
                      )}
                    </td>
                    <td className="cen">
                      <span className="acciones-fila">
                        <button
                          type="button"
                          className="btn btn-xs btn-contorno"
                          disabled={posicion === 0 || enviando}
                          onClick={() => {
                            void operar(() => moverBanner(banner.id, 'subir'));
                          }}
                          aria-label={`Subir ${banner.textoAlternativo}`}
                        >
                          <Icono nombre="chevron" className="gira-arriba" />
                        </button>
                        <button
                          type="button"
                          className="btn btn-xs btn-contorno"
                          disabled={posicion === fila.length - 1 || enviando}
                          onClick={() => {
                            void operar(() => moverBanner(banner.id, 'bajar'));
                          }}
                          aria-label={`Bajar ${banner.textoAlternativo}`}
                        >
                          <Icono nombre="chevron" className="gira-abajo" />
                        </button>
                      </span>
                    </td>
                    <td>
                      <button
                        type="button"
                        className="interruptor-boton"
                        aria-pressed={banner.activo}
                        disabled={enviando}
                        onClick={() => {
                          void operar(() => actualizarBanner(banner.id, { activo: !banner.activo }));
                        }}
                      >
                        <span className={banner.activo ? 'interruptor on' : 'interruptor'} />
                        <span>{banner.activo ? 'En la portada' : 'Apagado'}</span>
                      </button>
                    </td>
                    <td className="der">
                      <span className="acciones-fila">
                        <button
                          type="button"
                          className="btn btn-xs btn-contorno"
                          onClick={() => {
                            setEditando(banner);
                            setFalla(null);
                            setFormAbierto(true);
                          }}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn btn-xs btn-peligro"
                          onClick={() => {
                            setFalla(null);
                            setPorEliminar(banner);
                          }}
                        >
                          Eliminar
                        </button>
                      </span>
                    </td>
                  </tr>
                );
              })}
            </Tabla>
          ) : null}
        </PanelAdmin>

        {/* ---------- foto de la banda "quienes somos" (ADM-05) ---------- */}

        {configuracion !== null ? (
          <FotoTiendaPortada configuracion={configuracion} alGuardar={setConfiguracionGuardada} />
        ) : null}

        <div className="panel-doble">
          {/* ---------- productos destacados (ADM-06) ---------- */}

          <PanelAdmin
            titulo="Productos destacados"
            bajada={
              maximoDestacados !== null ? `La portada muestra hasta ${maximoDestacados}` : undefined
            }
            extra={
              maximoDestacados !== null && productos.length > maximoDestacados ? (
                <span className="etq etq-alerta">
                  {productos.length - maximoDestacados} no se ven
                </span>
              ) : null
            }
          >
            {configuracionInicial.estado === 'error' ? (
              <AvisoError error={configuracionInicial.error} />
            ) : null}
            {configuracion !== null ? (
              <MaximoDestacados
                configuracion={configuracion}
                alGuardar={setConfiguracionGuardada}
                alFallar={setFalla}
              />
            ) : null}

            {destacados.estado === 'cargando' ? <Cargando que="los destacados" /> : null}
            {destacados.estado === 'error' ? <AvisoError error={destacados.error} /> : null}

            <p className="parrafo-panel">
              Para destacar uno nuevo, búscalo en{' '}
              <Link to={rutas.administracion.productos()}>Productos</Link> y marcalo ahi: esa tabla
              tiene el buscador de todo el catálogo.
            </p>

            {destacados.datos !== null && productos.length === 0 ? (
              <p className="parrafo-panel">
                No hay ninguno marcado, así que la portada no muestra la fila de destacados.
              </p>
            ) : null}

            <ul className="lista-marcados">
              {productos.map((producto, indice) => (
                <li key={producto.id} className={fueraDePortada(indice) ? 'fuera' : undefined}>
                  <span className="marcado-nombre">
                    <span className="n">{producto.nombre}</span>
                    <span className="s">
                      {producto.sku}
                      {fueraDePortada(indice) ? ' · fuera de la portada' : ''}
                    </span>
                  </span>
                  <button
                    type="button"
                    className="btn btn-xs btn-contorno"
                    disabled={enviando}
                    onClick={() => {
                      void operar(() => destacarProducto(producto.id, false));
                    }}
                  >
                    Quitar
                  </button>
                </li>
              ))}
            </ul>
          </PanelAdmin>

          {/* ---------- categorias destacadas (ADM-06) ---------- */}

          <PanelAdmin
            titulo="Categorías destacadas"
            bajada={`La portada muestra hasta ${CATEGORIAS_EN_PORTADA}`}
          >
            {categorias.estado === 'cargando' ? <Cargando que="las categorías" /> : null}
            {categorias.estado === 'error' ? <AvisoError error={categorias.error} /> : null}

            {categorias.datos !== null && marcadas.length === 0 ? (
              <p className="parrafo-panel">
                Sin ninguna marcada, la portada se llena sola con las categorías que más
                productos tienen.
              </p>
            ) : null}

            <ul className="lista-marcados">
              {marcadas.map((categoria, indice) => (
                <li key={categoria.id} className={indice >= CATEGORIAS_EN_PORTADA ? 'fuera' : undefined}>
                  <span className="marcado-nombre">
                    <span className="n">{categoria.nombre}</span>
                    <span className="s">
                      {categoria.lineaNombre} · {numero(categoria.cantidadProductos)} productos
                      {indice >= CATEGORIAS_EN_PORTADA ? ' · fuera de la portada' : ''}
                    </span>
                  </span>
                  <span className="acciones-fila">
                    <button
                      type="button"
                      className="btn btn-xs btn-contorno"
                      disabled={enviando}
                      onClick={() => {
                        setFalla(null);
                        setEditandoSeoCategoria(categoria);
                      }}
                    >
                      Editar SEO
                    </button>
                    <button
                      type="button"
                      className="btn btn-xs btn-contorno"
                      disabled={enviando}
                      onClick={() => {
                        void operar(() => destacarCategoria(categoria.id, false));
                      }}
                    >
                      Quitar
                    </button>
                  </span>
                </li>
              ))}
            </ul>

            <div className="campo">
              <label htmlFor="buscar-categoria">Destacar otra categoría</label>
              <input
                id="buscar-categoria"
                type="search"
                autoComplete="off"
                placeholder="Escribe el nombre"
                value={busquedaCategoria}
                onChange={(evento) => {
                  setBusquedaCategoria(evento.target.value);
                }}
              />
            </div>

            {termino.length > 0 ? (
              <ul className="lista-marcados">
                {coincidencias.map((categoria) => (
                  <li key={categoria.id}>
                    <span className="marcado-nombre">
                      <span className="n">{categoria.nombre}</span>
                      <span className="s">
                        {categoria.lineaNombre} · {numero(categoria.cantidadProductos)} productos
                      </span>
                    </span>
                    <span className="acciones-fila">
                      <button
                        type="button"
                        className="btn btn-xs btn-contorno"
                        disabled={enviando}
                        onClick={() => {
                          setFalla(null);
                          setEditandoSeoCategoria(categoria);
                        }}
                      >
                        Editar SEO
                      </button>
                      <button
                        type="button"
                        className="btn btn-xs btn-primario"
                        disabled={enviando || categoria.cantidadProductos === 0}
                        title={
                          categoria.cantidadProductos === 0
                            ? 'No tiene productos publicados: la portada la descarta'
                            : undefined
                        }
                        onClick={() => {
                          void operar(async () => {
                            await destacarCategoria(categoria.id, true);
                            setBusquedaCategoria('');
                          });
                        }}
                      >
                        Destacar
                      </button>
                    </span>
                  </li>
                ))}
                {coincidencias.length === 0 ? (
                  <li>
                    <span className="celda-sub">Ninguna categoría sin destacar coincide.</span>
                  </li>
                ) : null}
              </ul>
            ) : null}
          </PanelAdmin>
        </div>
      </div>

      <FormularioBanner
        banner={editando}
        abierto={formAbierto}
        enviando={enviando}
        falla={formAbierto ? falla : null}
        onCerrar={() => {
          setFormAbierto(false);
        }}
        onCrear={(datos) => {
          void crearBannerNuevo(datos);
        }}
        onActualizar={(datos) => {
          void editarBanner(datos);
        }}
      />

      {/*
        Eliminar no tiene vuelta atras. Apagar el banner deja la imagen cargada
        para la campana que vuelve, y por eso la confirmacion lo ofrece.
      */}
      <Modal
        titulo="Eliminar este banner"
        bajada="No se puede deshacer"
        abierto={porEliminar !== null}
        onCerrar={() => {
          if (!enviando) setPorEliminar(null);
        }}
      >
        {falla !== null ? (
          <p className="acceso-error" role="alert">
            <Icono nombre="alerta" />
            <span>{falla}</span>
          </p>
        ) : null}

        <p className="parrafo-panel">
          Se va a borrar <strong>{porEliminar?.titulo ?? porEliminar?.textoAlternativo}</strong>{' '}
          de la portada. Si es una campaña que vuelve más adelante, conviene apagarlo en vez de
          eliminarlo.
        </p>

        <div className="modal-acciones">
          <button
            type="button"
            className="btn btn-contorno"
            disabled={enviando}
            onClick={() => {
              setPorEliminar(null);
            }}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-peligro"
            disabled={enviando}
            onClick={() => {
              const banner = porEliminar;
              if (banner === null) return;
              void operar(async () => {
                await eliminarBanner(banner.id);
                setPorEliminar(null);
              });
            }}
          >
            {enviando ? 'Eliminando…' : 'Eliminar el banner'}
          </button>
        </div>
      </Modal>

      <FormularioSeo
        entidad={editandoSeoCategoria}
        titulo={editandoSeoCategoria !== null ? `SEO de ${editandoSeoCategoria.nombre}` : ''}
        bajada="Título y descripción del resultado en buscadores"
        placeholderTitulo={editandoSeoCategoria?.nombre ?? ''}
        placeholderDescripcion="Se genera sola si se deja vacío"
        enviando={enviando}
        falla={editandoSeoCategoria !== null ? falla : null}
        onCerrar={() => {
          setEditandoSeoCategoria(null);
        }}
        onGuardar={(datos) => {
          void guardarSeoCategoria(datos);
        }}
      />
    </>
  );
}
